import { keyframes } from '@emotion/core';
import styled from '@emotion/styled';
import { Step, StepLabel as _StepLabel, Stepper as _Stepper, Typography } from '@material-ui/core';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp';
import { createElement, FC, Fragment, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import ProjectEditingStep from '../../../entity/ProjectEditingStep';
import { convertStepToOrder } from '../../../utility/workflowStepConvert';
import { useGetIsPC } from '../../../utilityHooks/useGetIsPC';
import { SPACING_UNIT } from '../../constant/spacing';
import mq from '../../styleUtility/mediaquery';

type Props = {
  currentStep: ProjectEditingStep;
  workflowSteps: ProjectEditingStep[];
  handleClickStep: (step: ProjectEditingStep) => void;
  className?: string;
};

const WorkflowStepper: FC<Props> = ({ currentStep, workflowSteps, handleClickStep, className }) => {
  const [isOpen, setOpen] = useState(false);
  const classNameIsOpen = useMemo(() => (isOpen ? 'isOpen' : ''), [isOpen]);
  const classNameAndIsOpen = useMemo(() => [className, isOpen ? 'isOpen' : ''].join(' '), [className, isOpen]);

  const activeStep = convertStepToOrder(currentStep, workflowSteps);
  const isPC = useGetIsPC();
  const memoizedHandleClickStep = useCallback((step: ProjectEditingStep) => {
    handleClickStep(step);
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      {isPC ? (
        <Root>
          <Stepper activeStep={activeStep} orientation={'vertical'} className={className}>
            {workflowSteps.map((step, index) => (
              <Step key={index} onClick={() => memoizedHandleClickStep(step)}>
                {index > activeStep ? (
                  <StepLabel>
                    <FormattedMessage id={`project_editing_page.stepper.${step}`} />
                  </StepLabel>
                ) : (
                  <ClickableStepLabel>
                    <ClickableText>
                      <FormattedMessage id={`project_editing_page.stepper.${step}`} />
                    </ClickableText>
                  </ClickableStepLabel>
                )}
              </Step>
            ))}
          </Stepper>
        </Root>
      ) : (
        <>
          <Root>
            <Stepper activeStep={activeStep} orientation={'horizontal'} className={className}>
              {workflowSteps.map((step, index) => (
                <Step key={index} onClick={() => memoizedHandleClickStep(step)}>
                  <StepLabel />
                </Step>
              ))}
            </Stepper>
            <ArrowDown style={{ marginLeft: '8px' }} onClick={() => setOpen(true)} />
          </Root>
          <>
            <RootColumn className={classNameIsOpen}>
              <ArrowUpWrap onClick={() => setOpen(false)} />
              <StepperColumn activeStep={activeStep} orientation={'vertical'} className={classNameAndIsOpen}>
                {workflowSteps.map((step, index) => (
                  <Step key={index} onClick={() => memoizedHandleClickStep(step)}>
                    <StepLabel className={classNameIsOpen}>
                      <FormattedMessage id={`project_editing_page.stepper.${step}`} />
                    </StepLabel>
                  </Step>
                ))}
              </StepperColumn>
            </RootColumn>
          </>
        </>
      )}
    </>
  );
};

const Root = styled('div')`
  ${mq('tab')} {
    position: relative;
  }
`;

const fadeInDown = keyframes({
  from: {
    opacity: 0,
    transform: 'translateY(-68px)',
  },

  to: {
    opacity: 1,
    transform: 'translateY(0px)',
  },
});

const RootColumn = styled('div')`
  position: relative;
  display: none;
  margin-top: -68px;

  &.isOpen {
    display: block;
    animation: ${fadeInDown} 0.3s ease-out forwards;
  }
`;

const Stepper = styled(_Stepper)`
  ${mq('sp')} {
    padding: 20px 20px 16px 5px;
  }
`;

const StepperColumn = styled(_Stepper)`
  &.isOpen {
    padding: 24px;
    animation: ${fadeInDown} 0.3s ease-out both;
  }
`;

const StepLabel = styled(_StepLabel)`
  margin-bottom: ${SPACING_UNIT}px;
  cursor: pointer;

  ${mq('sp')} {
    > .MuiStepLabel-iconContainer {
      padding-right: 0;
      box-shadow: 2px 2px 2px rgba(0, 0, 0, 0.4);
      border-radius: 50%;
    }

    &.Mui-disabled {
      > .MuiStepLabel-iconContainer {
        box-shadow: none;
      }
    }

    &.isOpen {
      > .MuiStepLabel-iconContainer {
        margin-right: 8px;
      }
    }
  }
`;

const ArrowDown = styled(KeyboardArrowDown)`
  position: absolute;
  margin: auto;
  top: -3px;
  bottom: 0;
  right: 0;
`;

const ArrowUpWrap = styled(KeyboardArrowUp)`
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 1;
`;

const ClickableStepLabel = styled(_StepLabel)`
  margin-bottom: ${SPACING_UNIT}px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ClickableText = styled(Typography)`
  &:hover {
    font-weight: bold;
  }
`;

export default WorkflowStepper;
